import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeesAPI } from "../../../Redux/Apis/employeeApi";
import { checkPermission, hasAdminAccess, permissionType } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import Loader from "../../atoms/Loader/Loader";
import { COMPANY_PROFILES_UPDATED_EVENT } from "../../../app/utils/company/companyProfilesStorage";
import { fetchCompanyProfiles } from "../../../app/utils/billing/companyDetailsApi";
import {
  createClientProfileApi,
  deleteClientProfileApi,
  fetchClientProfiles,
  updateClientProfileApi,
} from "../../../app/utils/billing/clientManagementApi";

const initialForm = {
  clientName: "",
  companyName: "",
  gstNumber: "",
  hsnCode: "",
  contactNumber: "",
  companyAddress: "",
  emailId: "",
  ccEmail: "",
  bccEmail: "",
  projectInitiationDate: "",
  onboardedCompanyId: "",
  resources: [],
};

const validationRules = {
  gstNumber: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  contactNumber: /^[0-9]{10}$/,
  emailId: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  projectInitiationDate: /^(\d{2})\/(\d{2})\/(\d{4})$/,
};

const requiredFields = [
  ["clientName", "Client Name"],
  ["companyName", "Company Name"],
  ["emailId", "Email ID"],
  ["companyAddress", "Company Address"],
  ["onboardedCompanyId", "Client Onboarded With Company"],
];

const formatValue = (key, value) => {
  if (key === "gstNumber") return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (key === "contactNumber") return value.replace(/\D/g, "").slice(0, 10);
  if (key === "projectInitiationDate") {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  return value;
};

const isValidProjectDate = (value) => {
  const match = String(value || "").match(validationRules.projectInitiationDate);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const isValidEmailList = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  return raw
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => validationRules.emailId.test(email));
};

const getCompanyLabel = (company) =>
  company?.companyName || company?.name || company?.emailId || "Company";

const formatPrice = (value) => {
  const number = Number(value);
  if (Number.isNaN(number)) return "Rs. 0";
  return `Rs. ${number.toLocaleString("en-IN")}`;
};

const ClientManagementPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth.auth);
  const { employees } = useSelector((state) => state.employee);
  const [form, setForm] = useState(initialForm);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [selectedResourcePrice, setSelectedResourcePrice] = useState("");
  const [selectedResourceNote, setSelectedResourceNote] = useState("");
  const [showResourceFields, setShowResourceFields] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canCreateClient =
    hasAdminAccess(user) || checkPermission(permissionType.CLIENT_MANAGEMENT, "create");
  const canUpdateClient =
    hasAdminAccess(user) || checkPermission(permissionType.CLIENT_MANAGEMENT, "update");
  const canDeleteClient =
    hasAdminAccess(user) || checkPermission(permissionType.CLIENT_MANAGEMENT, "delete");

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientList, companyList] = await Promise.all([
        fetchClientProfiles(),
        fetchCompanyProfiles(),
      ]);
      setClients(clientList);
      setCompanies(companyList);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to load client data.");
      setClients([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    dispatch(getEmployeesAPI({ page: 1, limit: 500, filterBy: "active" }));
    const onCompaniesUpdated = () => {
      fetchCompanyProfiles()
        .then(setCompanies)
        .catch(() => {});
    };
    window.addEventListener(COMPANY_PROFILES_UPDATED_EVENT, onCompaniesUpdated);
    return () => window.removeEventListener(COMPANY_PROFILES_UPDATED_EVENT, onCompaniesUpdated);
  }, [dispatch]);

  const companyOptions = useMemo(
    () =>
      companies.map((company, index) => {
        const value = String(company.id || company.gstNumber || `${getCompanyLabel(company)}-${index}`);
        return {
          value,
          label: getCompanyLabel(company),
        };
      }),
    [companies]
  );

  const employeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : [])
        .filter((employee) => Number(employee?.roleId) !== 1)
        .map((employee) => ({
          value: String(employee.userId || employee.id),
          label: `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email,
        }))
        .filter((option) => option.value && option.label),
    [employees]
  );

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: formatValue(key, value) }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const openCreateForm = () => {
    setForm(initialForm);
    setErrors({});
    setEditingClientId(null);
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setShowResourceFields(false);
    setShowForm(true);
  };

  const openEditForm = (client) => {
    if (!canUpdateClient) {
      showErrorToast("You do not have permission to update client details.");
      return;
    }
    setForm({ ...initialForm, ...client });
    setErrors({});
    setEditingClientId(client.id);
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setShowResourceFields(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setForm(initialForm);
    setErrors({});
    setEditingClientId(null);
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setShowResourceFields(false);
    setShowForm(false);
  };

  const openResourceFields = () => {
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setErrors((current) => ({ ...current, resourceEmployee: "", resourcePrice: "" }));
    setShowResourceFields(true);
  };

  const closeResourceFields = () => {
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setErrors((current) => ({ ...current, resourceEmployee: "", resourcePrice: "" }));
    setShowResourceFields(false);
  };

  const saveResource = () => {
    const nextErrors = {};
    const employeeId = selectedResourceId ? String(selectedResourceId) : "";
    const selectedEmployee = employeeOptions.find((option) => option.value === employeeId);
    const price = Number(selectedResourcePrice);

    if (!employeeId || !selectedEmployee) {
      nextErrors.resourceEmployee = "Employee selection is required.";
    }
    if (selectedResourcePrice === "" || Number.isNaN(price)) {
      nextErrors.resourcePrice = "Per Resource Price must be numeric.";
    } else if (price < 0) {
      nextErrors.resourcePrice = "Per Resource Price cannot be negative.";
    }
    if (form.resources.some((resource) => String(resource.employeeId) === employeeId)) {
      nextErrors.resourceEmployee = "This employee is already allocated to the client.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors((current) => ({ ...current, ...nextErrors }));
      showErrorToast(Object.values(nextErrors)[0]);
      return;
    }

    setForm((current) => ({
      ...current,
      resources: [
        ...current.resources,
        {
          employeeId,
          employeeName: selectedEmployee.label,
          price,
          note: selectedResourceNote.trim(),
        },
      ],
    }));
    setSelectedResourceId(null);
    setSelectedResourcePrice("");
    setSelectedResourceNote("");
    setShowResourceFields(false);
    setErrors((current) => ({
      ...current,
      resourceEmployee: "",
      resourcePrice: "",
      resources: "",
    }));
  };

  const removeResource = (employeeId) => {
    setForm((current) => ({
      ...current,
      resources: current.resources.filter((resource) => resource.employeeId !== employeeId),
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach(([key, label]) => {
      if (!String(form[key] || "").trim()) {
        nextErrors[key] = `${label} is required.`;
      }
    });

    if (form.gstNumber && !validationRules.gstNumber.test(form.gstNumber)) {
      nextErrors.gstNumber = "Enter GST in valid format, e.g. 22AAAAA0000A1Z5.";
    }
    if (
      String(form.contactNumber || "").trim() &&
      !validationRules.contactNumber.test(form.contactNumber)
    ) {
      nextErrors.contactNumber = "Contact Number must be exactly 10 digits.";
    }
    if (form.emailId && !isValidEmailList(form.emailId)) {
      nextErrors.emailId = "Enter valid email addresses separated by commas.";
    }
    if (!isValidEmailList(form.ccEmail)) {
      nextErrors.ccEmail = "Enter valid CC emails separated by commas.";
    }
    if (!isValidEmailList(form.bccEmail)) {
      nextErrors.bccEmail = "Enter valid BCC emails separated by commas.";
    }
    if (form.projectInitiationDate && !isValidProjectDate(form.projectInitiationDate)) {
      nextErrors.projectInitiationDate = "Project Initiation Date must be DD/MM/YYYY.";
    }
    if (!form.resources.length) {
      nextErrors.resources = "At least one Resource Allocation is required.";
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!editingClientId && !canCreateClient) {
      showErrorToast("You do not have permission to onboard clients.");
      return;
    }
    if (editingClientId && !canUpdateClient) {
      showErrorToast("You do not have permission to update client details.");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      showErrorToast(Object.values(validationErrors)[0]);
      return;
    }

    const selectedCompany = companyOptions.find((company) => company.value === form.onboardedCompanyId);
    const payload = {
      ...form,
      clientName: form.clientName.trim(),
      companyName: form.companyName.trim(),
      gstNumber: form.gstNumber.trim(),
      hsnCode: form.hsnCode.trim(),
      contactNumber: form.contactNumber.trim(),
      companyAddress: form.companyAddress.trim(),
      emailId: form.emailId.trim(),
      ccEmail: form.ccEmail.trim(),
      bccEmail: form.bccEmail.trim(),
      projectInitiationDate: form.projectInitiationDate.trim(),
      onboardedCompanyName: selectedCompany?.label || "",
      resources: form.resources.map((resource) => ({
        ...resource,
        price: Number(resource.price),
      })),
    };

    setSaving(true);
    try {
      if (editingClientId) {
        await updateClientProfileApi(editingClientId, payload);
        showSuccessToast("Client profile updated successfully.");
      } else {
        await createClientProfileApi(payload);
        showSuccessToast("Client onboarded successfully.");
      }
      await loadData();
      closeForm();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to save client profile.");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteClient = (client) => {
    if (!canDeleteClient) {
      showErrorToast("You do not have permission to delete client details.");
      return;
    }
    setClientToDelete(client);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    try {
      await deleteClientProfileApi(clientToDelete.id);
      if (String(editingClientId) === String(clientToDelete.id)) {
        closeForm();
      }
      setClientToDelete(null);
      await loadData();
      showSuccessToast("Client profile deleted successfully.");
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to delete client profile.");
    }
  };

  const renderInput = (key, label, placeholder, options = {}) => (
    <div className={options.fullWidth ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
        {label}
        {options.required ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        type={options.type || "text"}
        value={form[key]}
        onChange={(e) => updateField(key, e.target.value)}
        placeholder={placeholder}
        maxLength={options.maxLength}
        inputMode={options.inputMode}
        className={`search-input w-full !text-black ${errors[key] ? "!border-red-400" : ""}`}
      />
      {errors[key] ? <p className="mt-1 text-xs text-red-500">{errors[key]}</p> : null}
    </div>
  );

  return (
    <div className="client-management-page">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="page-title">Client Management</h1>
          <p className="page-subtitle">
            Onboard clients, associate them with a company, and manage resource pricing.
          </p>
        </div>
        {canCreateClient && !showForm ? (
          <button
            type="button"
            onClick={openCreateForm}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg self-start"
          >
            Onboard Client
          </button>
        ) : null}
      </div>

      {showForm ? (
        <div className="relative mb-6">
          {saving ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/75"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader className="!h-auto" />
            </div>
          ) : null}
        <form onSubmit={handleSubmit} className="card-border card-body space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-[#27314a] mb-4">
              {editingClientId ? "Update Client" : "Client Onboarding"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {renderInput("clientName", "Client Name", "John Doe", { required: true })}
              {renderInput("companyName", "Company Name", "ABC Technologies", { required: true })}
              {renderInput("gstNumber", "GST Number", "22AAAAA0000A1Z5")}
              {renderInput("hsnCode", "HSN Code", "Optional")}
              {renderInput("contactNumber", "Contact Number", "9876543210", {
                maxLength: 10,
                inputMode: "numeric",
              })}
              {renderInput("emailId", "Email ID", "client@company.com, accounts@company.com", {
                required: true,
              })}
              {renderInput("ccEmail", "Send Mail CC", "cc1@company.com, cc2@company.com", {
                type: "text",
              })}
              {renderInput("bccEmail", "Send Mail BCC", "bcc@company.com", {
                type: "text",
              })}
              {renderInput("projectInitiationDate", "Project Initiation Date", "DD/MM/YYYY")}
              <div>
                <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                  Client Onboarded With Company <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full mt-0"
                  options={companyOptions}
                  value={form.onboardedCompanyId || undefined}
                  onChange={(value) => updateField("onboardedCompanyId", value || "")}
                  placeholder="Select company"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                />
                {errors.onboardedCompanyId ? (
                  <p className="mt-1 text-xs text-red-500">{errors.onboardedCompanyId}</p>
                ) : null}
                {!companyOptions.length ? (
                  <p className="mt-1 text-xs text-[#6f7d97]">
                    Create a company profile in Company Details first.
                  </p>
                ) : null}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                  Company Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.companyAddress}
                  onChange={(e) => updateField("companyAddress", e.target.value)}
                  placeholder="Client company address"
                  rows={4}
                  className={`search-input w-full !h-auto !text-black !border !border-[#d7deed] !rounded-lg ${
                    errors.companyAddress ? "!border-red-400" : ""
                  }`}
                />
                {errors.companyAddress ? (
                  <p className="mt-1 text-xs text-red-500">{errors.companyAddress}</p>
                ) : null}
              </div>
            </div>
          </section>

          <section>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-[#27314a]">Resource Allocation</h2>
              {!showResourceFields ? (
                <button
                  type="button"
                  onClick={openResourceFields}
                  className="button bg-btn-primary text-white px-5 py-2 rounded-lg self-start"
                >
                  Add Resource
                </button>
              ) : null}
            </div>

            {showResourceFields ? (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr_auto_auto] gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                    Select Resource <span className="text-red-500">*</span>
                  </label>
                  <Select
                    className="w-full mt-0"
                    options={employeeOptions}
                    value={selectedResourceId || undefined}
                    onChange={(value) => {
                      setSelectedResourceId(value || null);
                      setErrors((current) => ({ ...current, resourceEmployee: "" }));
                    }}
                    placeholder="Select employee"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                  />
                  {errors.resourceEmployee ? (
                    <p className="mt-1 text-xs text-red-500">{errors.resourceEmployee}</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                    Per Resource Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={selectedResourcePrice}
                    onChange={(e) => {
                      setSelectedResourcePrice(e.target.value);
                      setErrors((current) => ({ ...current, resourcePrice: "" }));
                    }}
                    placeholder="50000"
                    className={`search-input w-full !text-black ${
                      errors.resourcePrice ? "!border-red-400" : ""
                    }`}
                  />
                  {errors.resourcePrice ? (
                    <p className="mt-1 text-xs text-red-500">{errors.resourcePrice}</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Note</label>
                  <input
                    type="text"
                    value={selectedResourceNote}
                    onChange={(e) => setSelectedResourceNote(e.target.value)}
                    placeholder="Optional note"
                    className="search-input w-full !text-black"
                  />
                </div>
                <button
                  type="button"
                  onClick={saveResource}
                  className="button bg-btn-primary text-white px-5 py-2 rounded-lg"
                >
                  Save Resource
                </button>
                <button
                  type="button"
                  onClick={closeResourceFields}
                  className="button border border-[#d7deed] bg-white text-[#27314a] px-5 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            ) : null}

            {errors.resources ? <p className="mt-2 text-xs text-red-500">{errors.resources}</p> : null}

            <div className="mt-4 overflow-x-auto rounded-xl border border-[#e6ebf6]">
              <table className="min-w-full divide-y divide-[#e6ebf6] bg-white text-sm">
                <thead className="bg-[#f9fbff] text-left text-[#5f6d8a]">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Resource</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Note</th>
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e6ebf6] text-[#27314a]">
                  {form.resources.length ? (
                    form.resources.map((resource) => (
                      <tr key={resource.employeeId}>
                        <td className="px-4 py-3">{resource.employeeName}</td>
                        <td className="px-4 py-3">{formatPrice(resource.price)}</td>
                        <td className="px-4 py-3">{resource.note || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeResource(resource.employeeId)}
                            className="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-600"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-4 py-4 text-center text-[#6f7d97]" colSpan={4}>
                        No resources allocated yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="button border border-[#d7deed] bg-white text-[#27314a] px-6 py-2 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="button bg-btn-primary text-white px-6 py-2 rounded-lg disabled:opacity-50"
            >
              {saving
                ? editingClientId
                  ? "Updating…"
                  : "Saving…"
                : editingClientId
                  ? "Update Client"
                  : "Save Client Profile"}
            </button>
          </div>
        </form>
        </div>
      ) : null}

      {!showForm ? (
      <section className="card-border card-body">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#27314a]">Client Profiles</h2>
            <p className="text-sm text-[#6f7d97]">
              {clients.length
                ? `${clients.length} client profile${clients.length > 1 ? "s" : ""} saved.`
                : "No client profiles created yet."}
            </p>
          </div>
        </div>

        {loading ? (
          <Loader className="!h-[30vh]" />
        ) : clients.length ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {clients.map((client) => (
              <article key={client.id} className="rounded-xl border border-[#e6ebf6] bg-[#f9fbff] p-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-[#27314a]">{client.clientName}</h3>
                    <p className="text-xs text-[#6f7d97]">{client.emailId}</p>
                  </div>
                  {(canUpdateClient || canDeleteClient) ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      {canUpdateClient ? (
                        <button
                          type="button"
                          onClick={() => openEditForm(client)}
                          className="rounded-md border border-[#d7deed] bg-white px-3 py-1 text-xs font-medium text-[#27314a]"
                        >
                          Edit
                        </button>
                      ) : null}
                      {canDeleteClient ? (
                        <button
                          type="button"
                          onClick={() => requestDeleteClient(client)}
                          className="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-600"
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#5f6d8a]">
                  <p>
                    <span className="font-medium text-[#27314a]">Company:</span> {client.companyName}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Contact:</span> {client.contactNumber}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">GST:</span> {client.gstNumber || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">HSN:</span> {client.hsnCode || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Initiation:</span>{" "}
                    {client.projectInitiationDate}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Onboarded With:</span>{" "}
                    {client.onboardedCompanyName || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Mail CC:</span> {client.ccEmail || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Mail BCC:</span> {client.bccEmail || "-"}
                  </p>
                  <p className="sm:col-span-2">
                    <span className="font-medium text-[#27314a]">Address:</span> {client.companyAddress}
                  </p>
                </div>

                <div className="mt-4 rounded-lg border border-[#e6ebf6] bg-white p-3">
                  <h4 className="mb-2 text-sm font-semibold text-[#27314a]">Resource Allocation</h4>
                  {client.resources?.length ? (
                    <div className="space-y-2">
                      {client.resources.map((resource) => (
                        <div
                          key={resource.employeeId}
                          className="flex items-start justify-between gap-3 text-sm text-[#5f6d8a]"
                        >
                          <div>
                            <span>{resource.employeeName}</span>
                            {resource.note ? (
                              <p className="mt-0.5 text-xs text-[#6f7d97]">{resource.note}</p>
                            ) : null}
                          </div>
                          <span className="font-medium text-[#27314a] shrink-0">{formatPrice(resource.price)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#6f7d97]">No resources allocated.</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-[#d7deed] bg-[#f9fbff] p-8 text-center">
            <h3 className="text-base font-semibold text-[#27314a] mb-1">No clients found</h3>
            <p className="text-sm text-[#6f7d97]">
              Use the Onboard Client button to create your first client profile.
            </p>
          </div>
        )}
      </section>
      ) : null}

      <ConfirmModal
        isOpen={Boolean(clientToDelete)}
        onClose={() => setClientToDelete(null)}
        onConfirm={confirmDeleteClient}
        title="Delete Client"
        content={`Are you sure you want to delete ${clientToDelete?.clientName || "this client"}?`}
      />
    </div>
  );
};

export default ClientManagementPage;
